import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useLenis } from "lenis/react";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { MEDIA_COUNTS, MEETINGS, STORY_LIST } from "./data.js";
import { InlineClip, StNotFound } from "./shared.jsx";
import "./stories.css";

function Block({ block }) {
  const reduce = useReducedMotion();
  if (block.type === "p") return <p>{block.text}</p>;
  if (block.type === "quote") {
    return (
      <figure className="st-quote">
        <motion.span
          className="st-quote__bar"
          aria-hidden="true"
          initial={reduce ? false : { scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ duration: 0.9, ease: EASE_OUT }}
        />
        <blockquote>
          <MaskWords as="p" text={block.text} />
        </blockquote>
        <figcaption>From this story</figcaption>
      </figure>
    );
  }
  if (block.type === "figure") {
    return (
      <ClipPhoto
        className={`st-inline ${block.wide ? "st-inline--wide" : ""}`}
        src={block.src}
        alt={block.alt}
        caption={block.caption}
        sizes={block.wide ? "(max-width: 900px) 100vw, 60vw" : "(max-width: 900px) 100vw, 40vw"}
      />
    );
  }
  if (block.type === "pair") {
    return (
      <div className="st-pair">
        {block.items.map((item) => (
          <ClipPhoto key={item.src} className="st-pair__item" src={item.src} alt={item.alt} caption={item.caption} sizes="(max-width: 700px) 100vw, 30vw" />
        ))}
      </div>
    );
  }
  if (block.type === "video") {
    return <InlineClip clip={block} className={block.wide ? "st-clip--wide" : ""} />;
  }
  if (block.type === "links") {
    return (
      <ul className="st-art__links">
        {block.items.map(([label, to]) => (
          <li key={to}>
            <Link to={to}><span>{label}</span><ArrowRight weight="bold" aria-hidden="true" /></Link>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function Article({ story }) {
  const reduce = useReducedMotion();
  const lenis = useLenis();
  const bodyRef = useRef(null);
  const [current, setCurrent] = useState(story.sections[0].id);
  const { scrollYProgress } = useScroll({ target: bodyRef, offset: ["start 0.3", "end 0.7"] });
  const progress = useTransform(scrollYProgress, (v) => Math.min(1, Math.max(0, v)));
  const others = STORY_LIST.filter((item) => item.slug !== story.slug);

  useEffect(() => {
    const targets = story.sections.map((section) => document.getElementById(section.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setCurrent(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -60% 0px" },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [story]);

  function jump(event, id) {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    if (lenis) lenis.scrollTo(target, { offset: -110, duration: reduce ? 0 : 1.1, immediate: reduce });
    else target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  }

  return (
    <main className="st-art">
      <motion.div className="st-progress" aria-hidden="true" style={{ scaleX: progress }} />
      <header className="st-art__head">
        <Link className="st-crumb" to="/stories"><ArrowLeft weight="bold" aria-hidden="true" /> Stories</Link>
        <p className="st-mono">{story.kind} · {story.date} · {story.minutes} min read</p>
        <MaskWords as="h1" text={story.title} onMount delay={0.05} />
        <motion.p
          className="st-art__standfirst"
          initial={reduce ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE_OUT }}
        >
          {story.standfirst}
        </motion.p>
        <p className="st-art__by">Published by Project GRID</p>
      </header>

      <figure className="st-art__lead">
        <motion.div
          className="st-art__lead-frame"
          initial={reduce ? false : { clipPath: "inset(0% 0% 100% 0% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ duration: 1.2, delay: 0.25, ease: EASE_OUT }}
        >
          <motion.div
            className="st-art__lead-zoom"
            initial={reduce ? false : { scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, delay: 0.25, ease: EASE_OUT }}
          >
            <Photo src={story.lead.src} alt={story.lead.alt} sizes="(max-width: 900px) 100vw, 92vw" priority />
          </motion.div>
        </motion.div>
        <figcaption>{story.lead.caption}</figcaption>
      </figure>

      <div className="st-art__body" ref={bodyRef}>
        <aside className="st-art__rail" aria-label="In this story">
          <p className="st-mono">In this story</p>
          <div className="st-art__track" aria-hidden="true">
            <motion.span style={reduce ? { scaleY: 1 } : { scaleY: progress }} />
          </div>
          <ol>
            {story.sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  onClick={(event) => jump(event, section.id)}
                  className={current === section.id ? "is-current" : undefined}
                  aria-current={current === section.id ? "true" : undefined}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </aside>
        <article className="st-art__text">
          {story.sections.map((section) => (
            <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`}>
              <h2 id={`${section.id}-title`}>{section.title}</h2>
              {section.blocks.map((block, index) => (
                <Fragment key={`${section.id}-${index}`}>
                  <Block block={block} />
                </Fragment>
              ))}
            </section>
          ))}
        </article>
      </div>

      <section className="st-art__next" aria-labelledby="st-next-title">
        <h2 id="st-next-title">Keep reading</h2>
        {others.map((other) => (
          <Link className="st-art__next-story" to={`/stories/${other.slug}`} key={other.slug}>
            <span className="st-art__next-img"><Photo src={other.lead.src} alt="" sizes="(max-width: 900px) 100vw, 45vw" /></span>
            <span className="st-art__next-copy">
              <span className="st-mono">{other.kind} · {other.date} · {other.minutes} min read</span>
              <strong>{other.title}</strong>
              <span>{other.summary}</span>
              <span className="gh-link">Read the story <ArrowRight weight="bold" aria-hidden="true" /></span>
            </span>
          </Link>
        ))}
        <ul className="st-art__also">
          <li><Link to="/stories"><strong>All stories</strong><span>Field notes and programme accounts</span><ArrowUpRight weight="bold" aria-hidden="true" /></Link></li>
          <li><Link to="/media"><strong>Photos</strong><span>{MEDIA_COUNTS.photos} photographs and {MEDIA_COUNTS.clips} clips</span><ArrowUpRight weight="bold" aria-hidden="true" /></Link></li>
          <li><Link to="/meetings"><strong>People we've met</strong><span>{MEETINGS.length} meeting records</span><ArrowUpRight weight="bold" aria-hidden="true" /></Link></li>
        </ul>
      </section>
    </main>
  );
}

export default function StoryPage() {
  const { slug } = useParams();
  const story = STORY_LIST.find((item) => item.slug === slug);
  if (!story) return <StNotFound />;
  return (
    <PageFrame className="rd-page st-page">
      <Seo title={story.title} path={`/stories/${slug}`} description={story.summary} type="article" />
      <Article story={story} key={story.slug} />
    </PageFrame>
  );
}
